import {
  type AnalyseTexte,
  type ControleTraduction,
  type EtapeTraduction,
  NOYAU_CODES,
  type NoyauCode,
  type NoyauEnCours,
  type NoyauLancement,
  type NoyauxTexte,
  type ResultatNoyau,
  type Session,
  type StatutTraduction,
  demandeAjoutNoyauxSchema,
  demandeTraductionSchema,
  traductionSchema,
} from '@rosetta/shared';
import type { ConfigTahiti } from '../config';
import {
  InterruptionIncompleteError,
  NoyauxIndisponiblesError,
  ServiceIndisponibleError,
  SessionExpireeError,
  TraductionIntrouvableError,
} from '../erreurs';
import type { RosettaApi } from '../rosetta-api';
import { analyser } from './analyse';
import { lireAnalyseTexte } from './analyse-texte';
import { login } from './auth';
import { type ContexteRosetta, resoudreContexte } from './contexte';
import { type LectureLancement, fusionnerControles, lireControle } from './controle';
import { type DocumentSource, TITRE_PAR_DEFAUT, creerDocument, lireDocument } from './documents';
import {
  type CorrespondanceTypes,
  type LectureEtapes,
  annulerEnCascade,
  lireCorrespondanceTypes,
  lireEtapesOuRepli,
} from './etapes';
import { lireHistorique } from './historique';
import { type OptionsHttp, creerAppelHttp } from './http';
import {
  type LancementDocument,
  type TraductionNoyau,
  etatsNoyaux,
  lireLancementsDocument,
  lireTraductionsLancement,
} from './noyaux-texte';
import { type CorrespondanceNoyaux, lireCorrespondanceNoyaux, lireResultats } from './resultats';
import {
  type CorrespondanceStatuts,
  type StatutTache,
  type TacheLue,
  annulerTache,
  classerStatut,
  creerTache,
  jamaisDemarree,
  lancerTache,
  lireCorrespondanceStatuts,
  lireTache,
  messageLisible,
} from './taches';

export interface OptionsApiTahiti extends OptionsHttp {
  /** Clock, injectable (tests). */
  maintenant?: () => number;
}

/**
 * The Tahiti client: the only implementation of `RosettaApi`. The application has no
 * simulator and no simulated mode; the tests bring a `fetch` that plays Tahiti
 * (`tests/lib/api/tahiti/`) and the browser tests intercept the HTTP calls
 * (`e2e/support/tahiti-doubles.ts`).
 *
 * One isolated function per Tahiti call, under this folder (`auth.ts`, `documents.ts`,
 * `taches.ts`, `contexte.ts`, `resultats.ts`, `historique.ts`, `etapes.ts`, `analyse-texte.ts`,
 * `controle.ts`), each with the Zod schema of its request and of its
 * response: whatever Tahiti sends goes through `parse` before leaving the client, and a
 * gap found at the first real call is fixed in one place. The session token goes out as
 * `Authorization: Bearer`; no service token or `api_token` ever goes in the front.
 *
 * Known risks: logging out does not revoke the token on the Tahiti side, and a token
 * kept in `localStorage` is readable by any script of the page.
 */
export function creerApiTahiti(config: ConfigTahiti, options: OptionsApiTahiti = {}): RosettaApi {
  const appel = creerAppelHttp(options);
  const maintenant = options.maintenant ?? (() => Date.now());

  // What does not change during a session is read once and kept: the noyaux of Tahiti,
  // the table of the task statuses, and the source document of a translation. A failed
  // read is never kept: the next call tries again.
  let noyaux: CorrespondanceNoyaux | null = null;
  let statuts: CorrespondanceStatuts | null = null;
  let types: CorrespondanceTypes | null = null;
  const documents = new Map<string, DocumentSource>();
  // The analysis and the control of a finished translation do not change either: kept for
  // the session.
  const analyses = new Map<string, AnalyseTexte>();
  // The outcome of each translation that ended, as `lireTraduction` read it with its translations
  // (#13): `listerTraductions` ranks it the same way. It does not change once the task has ended.
  const verdicts = new Map<string, 'terminee' | 'partielle' | 'echouee'>();
  // The control of a finished launch does not change either (#10): kept per launch, so that the
  // text of a document with several launches reads each control once (#34).
  const controles = new Map<string, ControleTraduction>();
  // The document of a task never changes: the one `lireTraduction` read is not read again (#34).
  const documentsDesTaches = new Map<string, string>();
  // The identifiers resolved by code (language, methodology, process, task type): read once
  // per session, on the first translation, and forgotten on login and logout.
  let contexte: Promise<ContexteRosetta> | null = null;

  async function correspondanceNoyaux(jeton: string): Promise<CorrespondanceNoyaux> {
    noyaux ??= await lireCorrespondanceNoyaux(appel, config, jeton);
    return noyaux;
  }

  /**
   * The status table, or `undefined` when Tahiti cannot give it: the spec's starting
   * hypothesis then applies (see `classerStatut`). A refused session still raises.
   */
  async function correspondanceStatuts(jeton: string): Promise<CorrespondanceStatuts | undefined> {
    if (statuts !== null) return statuts;
    try {
      statuts = await lireCorrespondanceStatuts(appel, config, jeton);
      return statuts;
    } catch (erreur) {
      if (erreur instanceof SessionExpireeError) throw erreur;
      // Explicit fallback, not kept: the next poll reads the table again.
      return undefined;
    }
  }

  async function correspondanceTypes(jeton: string): Promise<CorrespondanceTypes> {
    types ??= await lireCorrespondanceTypes(appel, config, jeton);
    return types;
  }

  /**
   * The steps of a running translation and whether one failed (#7, #13), or `undefined` when
   * Tahiti does not let us read them.
   */
  async function etapesDe(
    jeton: string,
    tacheId: string,
    choisis: readonly NoyauCode[],
    correspondance: CorrespondanceStatuts | undefined,
  ): Promise<LectureEtapes | undefined> {
    return lireEtapesOuRepli(
      appel,
      config,
      jeton,
      tacheId,
      choisis,
      () => correspondanceTypes(jeton),
      correspondance,
    );
  }

  async function contexteRosetta(jeton: string): Promise<ContexteRosetta> {
    contexte ??= resoudreContexte(appel, config, jeton);
    const enCours = contexte;
    try {
      return await enCours;
    } catch (erreur) {
      // A failed resolution is never kept: the next translation tries again.
      if (contexte === enCours) contexte = null;
      throw erreur;
    }
  }

  async function documentDe(jeton: string, id: string): Promise<DocumentSource> {
    const connu = documents.get(id);
    if (connu !== undefined) return connu;
    const lu = await lireDocument(appel, config, jeton, id);
    documents.set(id, lu);
    return lu;
  }

  /**
   * The translation of each chosen noyau of a task that ended, finished (`E`) or failed (`I`,
   * `F`): a failed launch may still have translated some noyaux, which are shown (#13). A noyau
   * with no text did not succeed. For a failed task a read that fails is no reason to hide the
   * failure behind "service indisponible": it reads as no translation at all.
   */
  async function resultatsDe(
    jeton: string,
    documentId: string,
    choisis: readonly NoyauCode[],
    statutTache: 'terminee' | 'echouee',
  ): Promise<ResultatNoyau[]> {
    let lus: Awaited<ReturnType<typeof lireResultats>>;
    try {
      lus = await lireResultats(
        appel,
        config,
        jeton,
        documentId,
        await correspondanceNoyaux(jeton),
      );
    } catch (erreur) {
      if (statutTache === 'terminee' || !(erreur instanceof ServiceIndisponibleError)) throw erreur;
      lus = [];
    }
    // The noyaux of the run, then the ones its text was translated towards by another run of the
    // same document (#11): the result shows every noyau of the text, in the charter order.
    const noyaux = NOYAU_CODES.filter(
      (noyau) => choisis.includes(noyau) || lus.some((resultat) => resultat.noyau === noyau),
    );
    return noyaux.map((noyau): ResultatNoyau => {
      const lu = lus.find((resultat) => resultat.noyau === noyau);
      return lu === undefined
        ? { noyau, statut: 'echouee' }
        : { noyau, statut: 'terminee', texte: lu.texte };
    });
  }

  /**
   * What a task that ended amounts to, once its translations are read (#13): every noyau of the run
   * translated is a finished translation, some of them a partial one, none a failed one (B3: a
   * task Tahiti says "Terminé" with nothing to read is a failure, not a finished translation).
   */
  function bilan(
    resultats: readonly ResultatNoyau[],
    propres: readonly NoyauCode[],
  ): StatutTraduction {
    // Only the noyaux of the run count: the ones other runs of the text added are not its own.
    const traduits = resultats.filter(
      (resultat) => propres.includes(resultat.noyau) && resultat.statut === 'terminee',
    ).length;
    if (traduits === 0) return 'echouee';
    return traduits === propres.length ? 'terminee' : 'partielle';
  }

  /**
   * The launches of the document of `tache` that belong to the user, or `undefined` when Tahiti
   * cannot give them (what is built on them, #36, is a comfort: the screen then shows none of it,
   * never a failure). A refused session still raises. One read, the one the choice of the noyaux
   * does (#11).
   */
  async function lancementsDuDocument(
    session: Session,
    tache: TacheLue,
    correspondance: CorrespondanceStatuts | undefined,
  ): Promise<LancementDocument[] | undefined> {
    try {
      return await lireLancementsDocument(
        appel,
        config,
        session.jeton,
        tache.documentId,
        session.utilisateur.id,
        correspondance,
      );
    } catch (erreur) {
      if (erreur instanceof ServiceIndisponibleError) return undefined;
      throw erreur;
    }
  }

  /**
   * The noyaux of the document of `tache` that another launch is translating right now (#36): what
   * the result and the analysis list with a spinner, towards the waiting screen of that launch.
   * Only a launch that still runs is read further, the way its own waiting screen is (its task,
   * then its steps), and it counts only when that read says "en cours": a launch that is blocked (a
   * step failed, #13), never started or already over has no spinner, its own page says what became
   * of it. **Every noyau of a launch that counts is listed, the ones whose translation is done
   * included**: the launch is not readable until its last step, the quality control, is over (the
   * waiting screen stays up), so a noyau is a result to open only once the launch can be read.
   * `propres` are the noyaux the launch of `tache` has the text of itself: another launch never
   * takes them. A refused session raises; a read that cannot be done lists nothing.
   */
  async function noyauxEnCoursDe(
    session: Session,
    tache: TacheLue,
    lancements: readonly LancementDocument[],
    propres: ReadonlySet<NoyauCode>,
    correspondance: CorrespondanceStatuts | undefined,
  ): Promise<NoyauEnCours[]> {
    const autres = lancements
      .filter((lancement) => lancement.id !== tache.id && lancement.statut === 'en_cours')
      .sort((a, b) => Number(b.id) - Number(a.id));

    const lectures = await Promise.all(
      autres.map(async (lancement) => {
        try {
          const lue = await lireTache(appel, config, session.jeton, lancement.id);
          if (lue === null || classerStatut(lue.statut, correspondance) !== 'en_cours')
            return false;
          if (jamaisDemarree(lue.statut, correspondance, lue.creeLe, maintenant())) return false;
          const etapes = await etapesDe(
            session.jeton,
            lancement.id,
            lancement.noyaux,
            correspondance,
          );
          return etapes?.bloquee !== true;
        } catch (erreur) {
          if (erreur instanceof ServiceIndisponibleError) return false;
          throw erreur;
        }
      }),
    );

    const pris = new Set<NoyauCode>();
    const enCours: NoyauEnCours[] = [];
    for (const [rang, lancement] of autres.entries()) {
      if (lectures[rang] !== true) continue;
      for (const noyau of lancement.noyaux) {
        if (propres.has(noyau) || pris.has(noyau)) continue;
        pris.add(noyau);
        enCours.push({ noyau, lancement: lancement.id });
      }
    }
    return NOYAU_CODES.flatMap((noyau) => enCours.filter((ligne) => ligne.noyau === noyau));
  }

  /**
   * The "Voir aussi :" of the waiting screen W1 (#36): the noyaux of the document already
   * translated by other launches, each with the most recent launch that ended well (`terminee`) and
   * carries it, which are not the noyaux of the launch that runs. No read of its own: it is built
   * on the list of the launches of the document. Pure.
   */
  function autresTraductionsDe(
    tache: TacheLue,
    lancements: readonly LancementDocument[],
  ): NoyauLancement[] {
    const recents = lancements
      .filter((lancement) => lancement.id !== tache.id && lancement.statut === 'terminee')
      .sort((a, b) => Number(b.id) - Number(a.id));
    return NOYAU_CODES.flatMap((noyau) => {
      if (tache.noyaux.includes(noyau)) return [];
      const porteur = recents.find((lancement) => lancement.noyaux.includes(noyau));
      return porteur === undefined ? [] : [{ noyau, lancement: porteur.id }];
    });
  }

  /**
   * Where each noyau stands for the text of the task `id` (#11): the runs of its document that
   * belong to the user, their translation sub-tasks and the rows of the document. Reads only.
   */
  async function noyauxDuTexte(session: Session, id: string): Promise<NoyauxTexte> {
    if (!/^\d+$/.test(id)) throw new TraductionIntrouvableError(id);
    const tache = await lireTache(appel, config, session.jeton, id);
    if (tache === null) throw new TraductionIntrouvableError(id);
    documentsDesTaches.set(tache.id, tache.documentId);

    const statutsTaches = await correspondanceStatuts(session.jeton);
    const [lus, resultats, typesTaches] = await Promise.all([
      lireLancementsDocument(
        appel,
        config,
        session.jeton,
        tache.documentId,
        session.utilisateur.id,
        statutsTaches,
      ),
      correspondanceNoyaux(session.jeton).then((noyaux) =>
        lireResultats(appel, config, session.jeton, tache.documentId, noyaux),
      ),
      // Without the types the sub-tasks cannot be told apart: each run then stands for its noyaux.
      correspondanceTypes(session.jeton).catch((erreur: unknown) => {
        if (erreur instanceof ServiceIndisponibleError) return undefined;
        throw erreur;
      }),
    ]);
    // The run asked about is always one of them, whatever the read of the runs answered.
    const lancements = lus.some((lancement) => lancement.id === tache.id)
      ? lus
      : [
          ...lus,
          {
            id: tache.id,
            statut: classerStatut(tache.statut, statutsTaches),
            noyaux: tache.noyaux,
          },
        ];

    const traductions = new Map<string, ReadonlyMap<NoyauCode, TraductionNoyau> | undefined>(
      await Promise.all(
        lancements.map(
          async (lancement) =>
            [
              lancement.id,
              typesTaches === undefined
                ? undefined
                : await lireTraductionsLancement(
                    appel,
                    config,
                    session.jeton,
                    lancement,
                    typesTaches,
                    statutsTaches,
                  ),
            ] as const,
        ),
      ),
    );
    return etatsNoyaux({ lancements, traductions, resultats });
  }

  /** The control of one launch, kept once it is read: none is kept, Tahiti may not have written it yet. */
  async function controleDuLancement(
    jeton: string,
    id: string,
  ): Promise<ControleTraduction | null> {
    const connu = controles.get(id);
    if (connu !== undefined) return connu;
    const lu = await lireControle(appel, config, jeton, id, () => correspondanceTypes(jeton));
    if (lu !== null) controles.set(id, lu);
    return lu;
  }

  /**
   * The control of the text of the task `id` (#34): one control per launch, so the launches of its
   * document (the same ones the choice of the noyaux reads, #11) are read in parallel, the
   * launch of `id` always among them, and the most recent launch that covers a noyau gives its
   * control. A launch still running or interrupted has none to read and is left alone. One whose
   * read fails (`ServiceIndisponibleError`) does not hide the others: its noyaux are reported
   * unread, and only when every read fails does the failure itself go to the screen. A refused
   * session always does. Reads only.
   */
  async function controleDuTexte(session: Session, id: string): Promise<ControleTraduction | null> {
    const { jeton } = session;
    let documentId = documentsDesTaches.get(id);
    let tache: TacheLue | null | undefined;
    if (documentId === undefined) {
      tache = await lireTache(appel, config, jeton, id);
      if (tache === null) throw new TraductionIntrouvableError(id);
      documentId = tache.documentId;
      documentsDesTaches.set(id, documentId);
    }

    const statutsTaches = await correspondanceStatuts(jeton);
    let lancements: LancementDocument[] = [];
    let listeIncomplete = false;
    try {
      lancements = await lireLancementsDocument(
        appel,
        config,
        jeton,
        documentId,
        session.utilisateur.id,
        statutsTaches,
      );
    } catch (erreur) {
      // The launches cannot be listed: the launch asked about is read alone, the rest is unknown.
      if (!(erreur instanceof ServiceIndisponibleError)) throw erreur;
      listeIncomplete = true;
    }
    if (!lancements.some((lancement) => lancement.id === id)) {
      tache ??= await lireTache(appel, config, jeton, id);
      if (tache === null) throw new TraductionIntrouvableError(id);
      lancements = [
        ...lancements,
        { id, statut: classerStatut(tache.statut, statutsTaches), noyaux: tache.noyaux },
      ];
    }
    // Only a launch that ended can have a control; the one of the URL is always read.
    const alire = lancements.filter(
      (lancement) =>
        lancement.id === id || lancement.statut === 'terminee' || lancement.statut === 'echouee',
    );

    const issues = await Promise.allSettled(
      alire.map((lancement) => controleDuLancement(jeton, lancement.id)),
    );
    const lectures: LectureLancement[] = [];
    let premiereErreur: unknown;
    for (const [rang, issue] of issues.entries()) {
      const lancement = alire[rang];
      if (lancement === undefined) continue;
      if (issue.status === 'fulfilled') {
        lectures.push({ id: lancement.id, noyaux: lancement.noyaux, controle: issue.value });
      } else if (issue.reason instanceof ServiceIndisponibleError) {
        premiereErreur ??= issue.reason;
        lectures.push({ id: lancement.id, noyaux: lancement.noyaux, controle: 'illisible' });
      } else {
        // A refused session, or anything that is not a failed read: it goes to the screen.
        throw issue.reason;
      }
    }
    if (lectures.every((lecture) => lecture.controle === 'illisible')) throw premiereErreur;
    return fusionnerControles(lectures, listeIncomplete);
  }

  return {
    async login(identifiants) {
      contexte = null;
      return login(appel, config, identifiants, maintenant());
    },

    // No Tahiti call: the session is erased locally (lib/deconnexion.ts) and the token
    // is NOT revoked on the Tahiti side. Only what was resolved for the session is dropped.
    async logout() {
      contexte = null;
    },

    async lancerTraduction(session, brute) {
      const demande = demandeTraductionSchema.parse(brute);
      const resolu = await contexteRosetta(session.jeton);
      const documentId = await creerDocument(appel, config, session.jeton, resolu, {
        ...(demande.titre === undefined ? {} : { titre: demande.titre }),
        texte: demande.texte,
      });
      // If a following call fails the document (and a task still waiting, if it is the
      // start that fails) stays orphaned in Tahiti: accepted, and reported to the user as a
      // failed translation. Nothing starts a task twice.
      const tacheId = await creerTache(appel, config, session.jeton, resolu, {
        titre: demande.titre?.trim() || TITRE_PAR_DEFAUT,
        documentId,
        noyaux: demande.noyaux,
      });
      await lancerTache(appel, config, session.jeton, tacheId);
      return tacheId;
    },

    async lireTraduction(session, id) {
      // Tahiti identifiers are numbers: anything else cannot be a translation.
      if (!/^\d+$/.test(id)) throw new TraductionIntrouvableError(id);

      const tache = await lireTache(appel, config, session.jeton, id);
      if (tache === null) throw new TraductionIntrouvableError(id);
      documentsDesTaches.set(tache.id, tache.documentId);

      const [document, correspondance] = await Promise.all([
        documentDe(session.jeton, tache.documentId),
        correspondanceStatuts(session.jeton),
      ]);
      const statutTache: StatutTache = classerStatut(tache.statut, correspondance);
      // Created and never started (B7, #13): a failure, not a translation running for ever.
      const nonDemarree =
        statutTache === 'en_cours' &&
        jamaisDemarree(tache.statut, correspondance, tache.creeLe, maintenant());

      let statut: StatutTraduction;
      let resultats: ResultatNoyau[];
      let etapes: EtapeTraduction[] | undefined;
      if (nonDemarree) {
        statut = 'echouee';
        resultats = tache.noyaux.map((noyau) => ({ noyau, statut: 'echouee' }));
      } else if (statutTache === 'terminee' || statutTache === 'echouee') {
        // The translations decide, whatever the letter (#13): a failed task that translated
        // every noyau is a finished translation (what failed came after them), one that
        // translated some of them a partial one.
        resultats = await resultatsDe(session.jeton, tache.documentId, tache.noyaux, statutTache);
        statut = bilan(resultats, tache.noyaux);
      } else {
        resultats = tache.noyaux.map((noyau) => ({ noyau, statut: statutTache }));
        statut = statutTache;
        if (statutTache === 'en_cours') {
          // While it runs: its steps (a read of the sub-tasks, none of the AI), and whether one
          // of them failed, which blocks it (#13).
          const lecture = await etapesDe(session.jeton, tache.id, tache.noyaux, correspondance);
          etapes = lecture?.etapes;
          if (lecture?.bloquee === true) statut = 'bloquee';
        }
      }
      // What the screen says of a translation that ended, the history says it too (B3, #13).
      if (statut === 'terminee' || statut === 'partielle' || statut === 'echouee') {
        verdicts.set(tache.id, statut);
      }
      // The noyaux of the run and, once it ended, those other runs of its text added (#11).
      const noyaux = resultats.map((resultat) => resultat.noyau);
      // The other launches of the document (#36): a text that can be read lists the noyaux another
      // launch is translating, the waiting screen the noyaux already translated by other launches.
      const lisible = statut === 'terminee' || statut === 'partielle';
      const attente = statut === 'en_cours' || statut === 'bloquee';
      const lancements =
        lisible || attente ? await lancementsDuDocument(session, tache, correspondance) : undefined;
      const enCoursAilleurs =
        lisible && lancements !== undefined
          ? await noyauxEnCoursDe(
              session,
              tache,
              lancements,
              new Set(
                resultats.flatMap((resultat) =>
                  resultat.statut === 'terminee' &&
                  resultat.texte !== undefined &&
                  tache.noyaux.includes(resultat.noyau)
                    ? [resultat.noyau]
                    : [],
                ),
              ),
              correspondance,
            )
          : [];
      const autresTraductions =
        attente && lancements !== undefined ? autresTraductionsDe(tache, lancements) : [];

      const message =
        statut === 'echouee' || statut === 'partielle' || statut === 'bloquee'
          ? messageLisible(tache.messageErreur)
          : undefined;
      const enCours = statut === 'en_cours' || statut === 'bloquee';

      return analyser(
        traductionSchema,
        {
          id: tache.id,
          ...(document.titre === undefined ? {} : { titre: document.titre }),
          texteOrigine: document.texte,
          noyaux,
          statut,
          resultats,
          ...(etapes === undefined ? {} : { etapes }),
          ...(enCoursAilleurs.length === 0 ? {} : { noyauxEnCours: enCoursAilleurs }),
          ...(autresTraductions.length === 0 ? {} : { autresTraductions }),
          ...(enCours && tache.creeLe !== undefined ? { commenceeLe: tache.creeLe } : {}),
          ...(message === undefined ? {} : { messageErreur: message }),
          ...(nonDemarree ? { nonDemarree: true } : {}),
        },
        'translation',
      );
    },

    async interrompreTraduction(session, id) {
      if (!/^[0-9]+$/.test(id)) throw new TraductionIntrouvableError(id);
      const tache = await lireTache(appel, config, session.jeton, id);
      if (tache === null) throw new TraductionIntrouvableError(id);

      // A translation that ended meanwhile is never cancelled: the screen shows its new state.
      const correspondance = await correspondanceStatuts(session.jeton);
      if (classerStatut(tache.statut, correspondance) !== 'en_cours') return false;

      // The task first, then everything it started that has not ended. If the task refuses,
      // nothing else is touched and the failure goes to the screen as it is.
      await annulerTache(appel, config, session.jeton, id);
      const restantes = await annulerEnCascade(appel, config, session.jeton, id, correspondance);
      if (restantes > 0) throw new InterruptionIncompleteError(restantes);
      return true;
    },

    async lireAnalyse(session, id) {
      // Same rule as the reading of a translation: only a number can be a Tahiti task.
      if (!/^\d+$/.test(id)) throw new TraductionIntrouvableError(id);
      const connue = analyses.get(id);
      if (connue !== undefined) return connue;

      const lue = await lireAnalyseTexte(appel, config, session.jeton, id, () =>
        correspondanceTypes(session.jeton),
      );
      // An answer with nothing readable in it is not kept: Tahiti may not have written it yet.
      if (lue.finalite !== null || lue.profil !== null || lue.invariants !== null) {
        analyses.set(id, lue);
      }
      return lue;
    },

    async lireControle(session, id) {
      if (!/^\d+$/.test(id)) throw new TraductionIntrouvableError(id);
      return controleDuTexte(session, id);
    },

    async lireNoyauxTexte(session, id) {
      return noyauxDuTexte(session, id);
    },

    async ajouterNoyaux(session, id, brute) {
      const demande = demandeAjoutNoyauxSchema.parse(brute);
      // Read again just before the paid write: the choice on screen may be out of date.
      const etats = await noyauxDuTexte(session, id);
      const pris = demande.noyaux.filter((noyau) =>
        etats.some(
          (ligne) =>
            ligne.noyau === noyau && (ligne.etat === 'traduit' || ligne.etat === 'en_cours'),
        ),
      );
      if (pris.length > 0) throw new NoyauxIndisponiblesError(pris);

      const tache = await lireTache(appel, config, session.jeton, id);
      if (tache === null) throw new TraductionIntrouvableError(id);
      const [resolu, document] = await Promise.all([
        contexteRosetta(session.jeton),
        documentDe(session.jeton, tache.documentId),
      ]);
      // The same document, so the same text and the same title; only the noyaux asked for.
      const ajout = await creerTache(appel, config, session.jeton, resolu, {
        titre: document.titre ?? TITRE_PAR_DEFAUT,
        documentId: tache.documentId,
        noyaux: demande.noyaux,
      });
      await lancerTache(appel, config, session.jeton, ajout);
      return ajout;
    },

    async listerTraductions(session) {
      const resumes = await lireHistorique(
        appel,
        config,
        session.jeton,
        session.utilisateur.id,
        await correspondanceStatuts(session.jeton),
        maintenant(),
      );
      // The history ranks a task by its status alone (reading the translations of every card
      // would cost a read each): a translation already read in this session is ranked the way
      // that read did, so that the card says what its page says (B3, #13).
      return resumes.map((resume) => {
        const verdict = verdicts.get(resume.id);
        return verdict !== undefined &&
          verdict !== resume.statut &&
          (resume.statut === 'terminee' || resume.statut === 'echouee')
          ? { ...resume, statut: verdict }
          : resume;
      });
    },
  };
}
